import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Home as HomeIcon, Building2, Users, Zap, Battery, 
  CheckCircle2, Shield, IndianRupee, Wrench, Award, BarChart3,
  Sun, Leaf, Star, ChevronLeft, ChevronRight
} from 'lucide-react';
import { calculateSolarSavings } from '../utils/calculatorEngine';

export const Home = () => {
  const [calcData, setCalcData] = useState({
    city: '',
    monthly_bill: '',
    roof_type: ''
  });
  const [calcResult, setCalcResult] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Image carousel configuration
  const carouselImages = [
    { name: 'IMG 1', ext: 'jpeg' },
    { name: 'IMG 2', ext: 'png' },
    { name: 'IMG 3', ext: 'jpeg' },
    { name: 'IMG 4', ext: 'jpeg' },
    { name: 'IMG 5', ext: 'jpeg' },
    { name: 'IMG 6', ext: 'jpeg' },
    { name: 'IMG 7', ext: 'jpeg' },
    { name: 'IMG 8', ext: 'jpeg' },
    { name: 'IMG 9', ext: 'jpeg' }
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const handleCalcSubmit = (e) => {
    e.preventDefault();
    try {
      const result = calculateSolarSavings(parseFloat(calcData.monthly_bill));
      setCalcResult(result);
    } catch (error) {
      console.error('Calculator error:', error);
    }
  };

  const solutions = [
    {
      icon: HomeIcon,
      title: 'Residential Solar',
      description: 'Perfect for homes, villas, and independent houses. Reduce your electricity bills by up to 80%.',
      path: '/solutions/residential-solar',
      gradient: 'from-lime-50 to-emerald-50'
    },
    {
      icon: Building2,
      title: 'Commercial Solar',
      description: 'Ideal for offices, factories, hospitals, and schools. High ROI with rapid payback.',
      path: '/solutions/commercial-solar',
      gradient: 'from-yellow-50 to-orange-50'
    },
    {
      icon: Users,
      title: 'Housing Society Solar',
      description: 'Specialized solutions for RWAs and housing societies with shared infrastructure.',
      path: '/solutions/housing-society-solar',
      gradient: 'from-blue-50 to-cyan-50'
    },
    {
      icon: Zap,
      title: 'On-Grid Solar',
      description: 'Connected to the grid with net metering. Sell excess power back to the grid.',
      path: '/solutions/on-grid-solar',
      gradient: 'from-purple-50 to-pink-50'
    },
    {
      icon: Battery,
      title: 'Off-Grid Solar',
      description: 'Complete independence from the grid with battery backup for remote areas.',
      path: '/solutions/off-grid-solar',
      gradient: 'from-indigo-50 to-violet-50'
    }
  ];

  const howItWorks = [
    {
      number: '01',
      title: 'Consultation & Site Survey',
      description: 'Our experts visit your property, assess roof structure, and understand your energy needs.'
    },
    {
      number: '02',
      title: 'Custom Design',
      description: 'We design a tailored solar system optimized for maximum efficiency and savings.'
    },
    {
      number: '03',
      title: 'Installation',
      description: 'Professional installation with minimal disruption. Completed in 2-3 days.'
    },
    {
      number: '04',
      title: 'Monitoring & Support',
      description: '24/7 remote monitoring and ongoing maintenance support for optimal performance.'
    }
  ];

  const whyChooseUs = [
    {
      icon: CheckCircle2,
      title: 'End-to-End Service',
      description: 'From design to installation to maintenance, we handle everything.'
    },
    {
      icon: IndianRupee,
      title: 'Easy Financing & EMI',
      description: 'Flexible payment options with zero-cost EMI available.'
    },
    {
      icon: Shield,
      title: 'Long Warranties',
      description: '25-year panel warranty and 5-year workmanship guarantee.'
    },
    {
      icon: Wrench,
      title: 'Expert Engineers',
      description: 'Certified professionals with 10+ years of solar experience.'
    },
    {
      icon: Award,
      title: 'Government Subsidies',
      description: 'We help you claim all eligible subsidies and incentives.'
    },
    {
      icon: BarChart3,
      title: 'Remote Monitoring',
      description: 'Track your system performance in real-time via mobile app.'
    }
  ];

  const testimonials = [
    {
      name: 'Shobharani',
      location: 'Homeowner - Warangal',
      rating: 5,
      text: 'Amigos Solar installed a 3kW rooftop system at my home, and the results are amazing. My monthly electricity bill has reduced drastically, and the installation process was smooth and professional. Highly satisfied!'
    },
    {
      name: 'Mahesh Goud',
      location: 'Homeowner – Warangal',
      rating: 5,
      text: 'From consultation to installation, the Amigos Solar team was transparent and supportive. The solar system is performing excellently, and I can already see significant savings on my power bills.'
    },
    {
      name: 'Sainath Reddy',
      location: 'Homeowner – Hyderabad',
      rating: 5,
      text: 'Choosing Amigos Solar was one of the best decisions I made. The 5kW system they installed is generating more power than expected, and their explanation regarding types is excellent'
    },
    {
      name: 'Sai Kiran',
      location: 'Homeowner – Hyderabad',
      rating: 5,
      text: 'Very professional service and quality equipment. Amigos Solar completed the installation on time, and my electricity expenses have gone. I would definitely recommend them.'
    }
  ];

  const blogPreviews = [
    {
      title: 'Solar 101: How Rooftop Solar Works in India',
      excerpt: 'Everything you need to know about solar panels, inverters, net metering, and government policies.',
      category: 'Guide',
      path: '/blog/solar-101-how-rooftop-solar-works'
    },
    {
      title: 'On-Grid vs Off-Grid vs Hybrid: Which Solar System Is Right for You?',
      excerpt: 'Understand the differences and choose the best system based on your needs and budget.',
      category: 'Comparison',
      path: '/blog/on-grid-vs-off-grid-vs-hybrid'
    },
    {
      title: 'Government Solar Subsidies Explained for Homeowners',
      excerpt: 'Learn about PM Surya Ghar scheme and how to claim up to ₹78,000 in subsidies.',
      category: 'Finance',
      path: '/blog/government-solar-subsidies'
    },
    {
      title: '5 Mistakes to Avoid Before Installing Solar Panels',
      excerpt: 'Common pitfalls that homeowners make and how to avoid them for maximum savings.',
      category: 'Tips',
      path: '/blog/5-mistakes-to-avoid'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden noise-bg" data-testid="hero-section">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1761472823286-9f6093ed6663?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMG1vZGVybiUyMGhvdXNlJTIwcm9vZiUyMGJsdWUlMjBza3l8ZW58MHx8fHwxNjczMzQ0MjR8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-7xl font-heading font-semibold text-primary tracking-tight leading-[1.1] mb-6">
                Switch to Solar. Cut Your Power Bills for Life.
              </h1>
              <p className="text-lg md:text-xl text-stone-600 leading-relaxed mb-8">
                Switch to solar with Amigos Solar and experience zero to negligible electricity bills without the hassle. Clean energy, transparent savings, and dependable support—right from day one.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-secondary text-black hover:bg-gradient-to-r hover:from-secondary hover:to-accent rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 text-center"
                  data-testid="hero-cta-consultation"
                >
                  Get Free Consultation
                </Link>
                <Link
                  to="/savings-calculator"
                  className="border-2 border-secondary text-black hover:bg-secondary hover:text-black rounded-full px-8 py-4 text-lg font-medium transition-all text-center"
                  data-testid="hero-cta-calculator"
                >
                  Estimate Your Savings
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-3xl p-8 shadow-hover">
                <h3 className="text-2xl font-heading font-semibold text-primary mb-6">Quick Savings Calculator</h3>
                <form onSubmit={handleCalcSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">District</label>
                    <select
                      value={calcData.city}
                      onChange={(e) => setCalcData({...calcData, city: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      required
                      data-testid="hero-calc-city-input"
                    >
                      <option value="">Select District</option>
                      <option value="Adilabad">Adilabad</option>
                      <option value="Bhadradri Kothagudem">Bhadradri Kothagudem</option>
                      <option value="Hanumakonda">Hanumakonda</option>
                      <option value="Hyderabad">Hyderabad</option>
                      <option value="Jagtial">Jagtial</option>
                      <option value="Jangaon">Jangaon</option>
                      <option value="Jayashankar Bhupalpally">Jayashankar Bhupalpally</option>
                      <option value="Jogulamba Gadwal">Jogulamba Gadwal</option>
                      <option value="Kamareddy">Kamareddy</option>
                      <option value="Karimnagar">Karimnagar</option>
                      <option value="Khammam">Khammam</option>
                      <option value="Kumuram Bheem">Kumuram Bheem</option>
                      <option value="Mahabubabad">Mahabubabad</option>
                      <option value="Mahabubnagar">Mahabubnagar</option>
                      <option value="Mancherial">Mancherial</option>
                      <option value="Medak">Medak</option>
                      <option value="Medchal-Malkajgiri">Medchal-Malkajgiri</option>
                      <option value="Mulugu">Mulugu</option>
                      <option value="Nagarkurnool">Nagarkurnool</option>
                      <option value="Nalgonda"> Nalgonda</option>
                      <option value="Narayanpet">Narayanpet</option>
                      <option value="Nirmal">Nirmal</option>
                      <option value="Nizamabad">Nizamabad</option>
                      <option value="Peddapalli">Peddapalli</option>
                      <option value="Rajanna Sircilla">Rajanna Sircilla</option>
                      <option value="Rangareddy">Rangareddy</option>
                      <option value="Sangareddy">Sangareddy</option>
                      <option value="Siddipet">Siddipet</option>
                      <option value="Suryapet">Suryapet</option>
                      <option value="Vikarabad">Vikarabad</option>
                      <option value="Wanaparthy">Wanaparthy</option>
                      <option value="Warangal">Warangal</option>
                      <option value="Yadadri Bhuvanagiri">Yadadri Bhuvanagiri</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Monthly Electricity Bill (₹)</label>
                    <input
                      type="number"
                      value={calcData.monthly_bill}
                      onChange={(e) => setCalcData({...calcData, monthly_bill: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="e.g., 5000"
                      required
                      data-testid="hero-calc-bill-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Roof Type</label>
                    <select
                      value={calcData.roof_type}
                      onChange={(e) => setCalcData({...calcData, roof_type: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      required
                      data-testid="hero-calc-roof-input"
                    >
                      <option value="">Select Roof Type</option>
                      <option value="RCC">RCC/Concrete</option>
                      <option value="Tin">Tin Shed</option>
                      <option value="Terrace">Terrace</option>
                      <option value="Ground">Ground Mount</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-secondary text-secondary-foreground hover:bg-[#fde047] rounded-full px-8 py-3 font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                    data-testid="hero-calc-submit-button"
                  >
                    Calculate Savings
                  </button>
                </form>

                {calcResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-6 bg-lime-50 rounded-2xl border border-lime-200"
                    data-testid="hero-calc-results"
                  >
                    <h4 className="font-heading font-semibold text-primary mb-4">Your Savings Potential</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-stone-600">System Size</p>
                        <p className="text-2xl font-bold text-primary">{calcResult.system_size_kw} kW</p>
                      </div>
                      <div>
                        <p className="text-sm text-stone-600">Monthly Savings</p>
                        <p className="text-2xl font-bold text-accent">₹{calcResult.monthly_savings}</p>
                      </div>
                      <div>
                        <p className="text-sm text-stone-600">Yearly Savings</p>
                        <p className="text-2xl font-bold text-accent">₹{calcResult.yearly_savings}</p>
                      </div>
                      <div>
                        <p className="text-sm text-stone-600">Payback Period</p>
                        <p className="text-2xl font-bold text-primary">{calcResult.payback_period} years</p>
                      </div>
                    </div>
                    <Link
                      to={`/savings-calculator?city=${encodeURIComponent(calcData.city)}&monthly_bill=${encodeURIComponent(calcData.monthly_bill)}&roof_type=${encodeURIComponent(calcData.roof_type)}`}
                      className="block text-center mt-4 text-primary hover:text-[#1a5c45] font-medium"
                      data-testid="hero-calc-detailed-link"
                    >
                      Get Detailed Savings Report →
                    </Link>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image Carousel - One at a Time */}
      <section className="py-20 bg-stone-100" data-testid="image-carousel-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-primary tracking-tight text-center mb-4">
            Our Solar Installations
          </h2>
          <p className="text-center text-stone-600 text-lg mb-12">
            Real projects, real results from across India
          </p>
          
          <div className="relative rounded-3xl overflow-hidden shadow-lg p-4 bg-white" style={{ aspectRatio: '18/9' }}>
            <motion.img
              key={currentImageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              src={process.env.PUBLIC_URL + `/${carouselImages[currentImageIndex].name}.${carouselImages[currentImageIndex].ext}`}
              alt={`Solar Installation ${currentImageIndex + 1}`}
              className="w-full h-full object-contain rounded-2xl"
            />
            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentImageIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-secondary/80 hover:bg-secondary text-white rounded-full flex items-center justify-center transition-all shadow-lg"
              data-testid="image-prev-button"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button
              onClick={() => setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-secondary/80 hover:bg-secondary text-white rounded-full flex items-center justify-center transition-all shadow-lg"
              data-testid="image-next-button"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentImageIndex ? 'bg-secondary w-8' : 'bg-white/50 hover:bg-white/70'
                  }`}
                  data-testid={`image-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20 md:py-32" data-testid="solutions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary tracking-tight mb-4">
              Solar Solutions for Every Need
            </h2>
            <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto">
              Whether you're a homeowner, business, or housing society, we have the perfect solar solution.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, index) => (
              <motion.div
                key={solution.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={solution.path} className="block">
                  <div className={`bg-gradient-to-br ${solution.gradient} rounded-3xl p-8 border border-stone-100 hover:shadow-hover transition-all duration-300 group h-full`}>
                    <solution.icon className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-2xl font-heading font-semibold text-primary mb-3">{solution.title}</h3>
                    <p className="text-stone-600 mb-4">{solution.description}</p>
                    <span className="inline-flex items-center text-primary font-medium group-hover:translate-x-2 transition-transform">
                      Learn More <ArrowRight className="w-4 h-4 ml-2" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32 bg-stone-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary tracking-tight mb-4">
              How It Works
            </h2>
            <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto">
              Four simple steps to start saving with solar energy.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-3xl p-8 shadow-card h-full">
                  <div className="text-6xl font-bold text-accent/20 mb-4">{step.number}</div>
                  <h3 className="text-xl font-heading font-semibold text-primary mb-3">{step.title}</h3>
                  <p className="text-stone-600">{step.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-accent/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary tracking-tight mb-4">
              Why Choose Amigos Solar
            </h2>
            <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto">
              We're not just solar installers. We're your partners in the journey to energy independence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 border border-stone-100 shadow-card hover:shadow-hover transition-all duration-300 group"
              >
                <feature.icon className="w-12 h-12 text-accent mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-heading font-semibold text-primary mb-3">{feature.title}</h3>
                <p className="text-stone-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32 bg-stone-50" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary tracking-tight mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg md:text-xl text-stone-600">
              Real experiences from real customers across India.
            </p>
          </motion.div>

          <div className="relative">
            <div className="bg-white rounded-3xl p-12 shadow-hover">
              <div className="flex justify-center mb-4">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-secondary fill-secondary" />
                ))}
              </div>
              <p className="text-xl text-stone-700 text-center mb-6 italic">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div className="text-center">
                <p className="font-heading font-semibold text-primary">{testimonials[currentTestimonial].name}</p>
                <p className="text-stone-600">{testimonials[currentTestimonial].location}</p>
              </div>
            </div>

            <div className="flex justify-center items-center space-x-4 mt-8">
              <button
                onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                className="w-12 h-12 bg-secondary text-white rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-secondary hover:to-accent transition-all"
                data-testid="testimonial-prev-button"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="flex space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentTestimonial ? 'bg-secondary w-8' : 'bg-stone-300'
                    }`}
                    data-testid={`testimonial-dot-${index}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                className="w-12 h-12 bg-secondary text-white rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-secondary hover:to-accent transition-all"
                data-testid="testimonial-next-button"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section className="py-20 md:py-32" data-testid="blog-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary tracking-tight mb-4">
              Latest from Our Blog
            </h2>
            <p className="text-lg md:text-xl text-stone-600">
              Expert insights, guides, and tips to help you make informed decisions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {blogPreviews.map((blog, index) => (
              <motion.div
                key={blog.path}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={blog.path} className="block">
                  <div className="bg-white rounded-3xl p-6 border border-stone-100 shadow-card hover:shadow-hover transition-all duration-300 group h-full">
                    <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary text-sm font-medium rounded-full mb-4">
                      {blog.category}
                    </span>
                    <h3 className="text-lg font-heading font-semibold text-primary mb-3 group-hover:text-secondary transition-colors">
                      {blog.title}
                    </h3>
                    <p className="text-stone-600 text-sm mb-4">{blog.excerpt}</p>
                    <span className="inline-flex items-center text-secondary font-medium text-sm group-hover:translate-x-2 transition-transform">
                      Read More <ArrowRight className="w-4 h-4 ml-2" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/blog"
              className="inline-flex items-center text-primary hover:text-[#1a5c45] font-medium text-lg"
              data-testid="blog-view-all-link"
            >
              View All Articles <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-32 relative overflow-hidden" data-testid="final-cta-section">
        <div 
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF8E7 100%)'
          }}
        />
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10 text-center text-black">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="inline-block mb-8"
            >
              <div className="w-24 h-24 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center shadow-lg">
                <Sun className="w-12 h-12 text-white" />
              </div>
            </motion.div>
            <h2 className="text-4xl md:text-6xl font-heading font-semibold tracking-tight mb-6 text-black">
              Start Your Solar Journey Today
            </h2>
            <p className="text-xl text-black/70 mb-12 max-w-2xl mx-auto leading-relaxed">
              Get a free consultation from our solar experts. No obligations, just honest advice tailored to your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-secondary text-black hover:bg-gradient-to-r hover:from-secondary hover:to-accent rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid="final-cta-quote-button"
              >
                Get Free Solar Quote
              </Link>
              <a
                href="tel:+911234567890"
                className="border-2 border-secondary text-secondary hover:bg-secondary hover:text-black hover:border-secondary rounded-full px-8 py-4 text-lg font-medium transition-all"
                data-testid="final-cta-call-button"
              >
                Talk to a Solar Expert
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
